import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import useRepo from 'hooks/useRepo';
import { M } from 'TOMA';
import s from './Crm.module.scss';
import ds from './CrmProspectDetail.module.scss';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  iInteraction,
  iInteractionForm,
  eCrmProspectStatus,
} from 'services/Crm/Crm.type';
import CrmInteractionForm from './CrmInteractionForm';
import { CustomLink } from 'components/CustomLink';
import {
  computeScore,
  computeTemperature,
  TEMPERATURE_EMOJI,
} from 'utils/crmScoring';

const STATUSES: eCrmProspectStatus[] = [
  'new',
  'contacted',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

function CrmProspectFileDetail(): JSX.Element {
  const navigate = useNavigate();
  const { prospectId } = useParams();
  const { crmRepository } = useApiContext();
  const { t } = useTranslation();
  const [reloadInteractions, setReloadInteractions] = useState(0);
  const [showForm, setShowForm] = useState(false);

  const {
    response: prospect,
    loading: prospectLoading,
    reload: reloadProspect,
  } = useRepo(
    async () => await crmRepository.getProspectFile(Number(prospectId)),
    [prospectId]
  );

  const { response: interactions = [], loading: interactionsLoading } = useRepo(
    async () => await crmRepository.getInteractions(Number(prospectId)),
    [prospectId, reloadInteractions]
  );

  const handleStatusChange = async (
    newStatus: eCrmProspectStatus
  ): Promise<void> => {
    try {
      await crmRepository.updateProspectStatus(Number(prospectId), newStatus);
      toast.success(t('crm:prospect_updated'));
      reloadProspect();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleCreateInteraction = async (
    data: iInteractionForm
  ): Promise<void> => {
    try {
      await crmRepository.createInteraction({
        ...data,
        prospect_file_id: Number(prospectId),
      });
      toast.success(t('crm:interaction_created'));
      setShowForm(false);
      setReloadInteractions((e) => e + 1);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDeleteInteraction = async (
    interactionId: number
  ): Promise<void> => {
    if (!window.confirm(t('crm:delete_interaction_confirm'))) return;
    try {
      await crmRepository.deleteInteraction(interactionId);
      toast.success(t('crm:interaction_deleted'));
      setReloadInteractions((e) => e + 1);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleConvert = async (): Promise<void> => {
    if (!window.confirm(t('crm:convert_confirm'))) return;
    try {
      await crmRepository.convertToExhibitor(Number(prospectId));
      toast.success(t('crm:convert_success'));
      reloadProspect();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const score =
    prospect != null ? computeScore(prospect, interactions?.length ?? 0) : 0;
  const temp =
    prospect != null ? computeTemperature(prospect) : ('cold' as const);
  const isConverted =
    prospect?.converted_exhibitor_id != null &&
    prospect.converted_exhibitor_id !== 0
      ? true
      : prospect?.converted_exhibitor_id === 0;

  return (
    <div>
      <M.Loading loading={prospectLoading} error={null} fullWiewport>
        {prospect != null && (
          <>
            {/* Header */}
            <div className={ds.header}>
              <button
                type="button"
                className={ds.backLink}
                onClick={() => navigate(-1)}
              >
                &larr; {t('crm:back_to_prospects')}
              </button>
              <div className={ds.headerMain}>
                <div>
                  <h1 className={ds.companyName}>
                    {prospect.company?.name ?? '-'}
                  </h1>
                  {prospect.contact != null && (
                    <p className={ds.contactName}>
                      {prospect.contact.first_name} {prospect.contact.last_name}
                      {prospect.contact.position != null &&
                        ` - ${prospect.contact.position}`}
                    </p>
                  )}
                </div>
                <div className={ds.headerActions}>
                  <span className={s.statusBadge} data-status={prospect.status}>
                    {t(`crm:status_${prospect.status}`)}
                  </span>
                  <span
                    className={s.priorityBadge}
                    data-priority={prospect.priority}
                  >
                    {t(`crm:priority_${prospect.priority}`)}
                  </span>
                  {prospect.status === 'won' && !isConverted && (
                    <button
                      type="button"
                      className={ds.convertBtn}
                      onClick={handleConvert}
                    >
                      &#127919; {t('crm:convert_to_exhibitor')}
                    </button>
                  )}
                  {isConverted && (
                    <span className={ds.convertedBadge}>
                      &#9989; {t('crm:already_converted')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Two-column layout */}
            <div className={ds.detailLayout}>
              {/* Main column */}
              <div className={ds.mainColumn}>
                {/* Company Info Card */}
                <div className={ds.card}>
                  <h3 className={ds.cardTitle}>{t('crm:company_info')}</h3>
                  <div className={ds.infoGrid}>
                    <div className={ds.infoItem}>
                      <span className={ds.infoLabel}>
                        {t('crm:company_name')}
                      </span>
                      <span className={ds.infoValue}>
                        {prospect.company != null ? (
                          <CustomLink
                            to={routes.crmCompanyDetail.to({
                              companyId: prospect.company.id,
                            })}
                            color="info"
                          >
                            {prospect.company.name}
                          </CustomLink>
                        ) : (
                          '-'
                        )}
                      </span>
                    </div>
                    {prospect.contact != null && (
                      <>
                        <div className={ds.infoItem}>
                          <span className={ds.infoLabel}>{t('crm:email')}</span>
                          <span className={ds.infoValue}>
                            {prospect.contact.email ?? '-'}
                          </span>
                        </div>
                        <div className={ds.infoItem}>
                          <span className={ds.infoLabel}>{t('crm:phone')}</span>
                          <span className={ds.infoValue}>
                            {prospect.contact.phone ?? '-'}
                          </span>
                        </div>
                      </>
                    )}
                    <div className={ds.infoItem}>
                      <span className={ds.infoLabel}>{t('crm:source')}</span>
                      <span className={ds.infoValue}>
                        {prospect.source ?? '-'}
                      </span>
                    </div>
                    <div className={ds.infoItem}>
                      <span className={ds.infoLabel}>{t('crm:status')}</span>
                      <select
                        value={prospect.status}
                        onChange={async (e) =>
                          await handleStatusChange(
                            e.target.value as eCrmProspectStatus
                          )
                        }
                        className={ds.statusSelect}
                      >
                        {STATUSES.map((st) => (
                          <option key={st} value={st}>
                            {t(`crm:status_${st}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {prospect.notes != null && prospect.notes !== '' && (
                    <div className={ds.notesSection}>
                      <span className={ds.infoLabel}>{t('crm:notes')}</span>
                      <p className={ds.notesText}>{prospect.notes}</p>
                    </div>
                  )}
                </div>

                {/* Interactions Section */}
                <div className={ds.card}>
                  <div className={ds.cardHeader}>
                    <h3 className={ds.cardTitle}>{t('crm:interactions')}</h3>
                    <button
                      type="button"
                      className={ds.addBtn}
                      onClick={() => setShowForm(true)}
                    >
                      + {t('crm:new_interaction')}
                    </button>
                  </div>

                  {showForm && (
                    <div className={ds.formWrap}>
                      <CrmInteractionForm
                        prospectFileId={Number(prospectId)}
                        onSubmit={handleCreateInteraction}
                        onCancel={() => setShowForm(false)}
                      />
                    </div>
                  )}

                  <M.Loading
                    loading={interactionsLoading}
                    error={null}
                    fullWiewport
                  >
                    {interactions != null && interactions.length > 0 ? (
                      <div className={s.timeline}>
                        {interactions.map((interaction: iInteraction) => (
                          <div key={interaction.id} className={s.timelineItem}>
                            <div className={s.timelineDate}>
                              {new Date(
                                interaction.interaction_date
                              ).toLocaleDateString()}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 4,
                              }}
                            >
                              <div>
                                <span className={s.timelineType}>
                                  {t(`crm:type_${interaction.type}`)}
                                </span>
                                <strong>{interaction.subject}</strong>
                              </div>
                              <button
                                type="button"
                                className={s.actionBtnDanger}
                                onClick={async () =>
                                  await handleDeleteInteraction(interaction.id)
                                }
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#ef4444"
                                  strokeWidth="2"
                                >
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                            {interaction.content != null &&
                              interaction.content !== '' && (
                                <p
                                  style={{
                                    fontSize: 14,
                                    color: '#6b7280',
                                    margin: 0,
                                  }}
                                >
                                  {interaction.content}
                                </p>
                              )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={ds.emptyState}>
                        {t('crm:no_interactions')}
                      </div>
                    )}
                  </M.Loading>
                </div>
              </div>

              {/* Sidebar */}
              <div className={ds.sidebar}>
                {/* Score Card */}
                <div className={ds.sidebarCard}>
                  <h4 className={ds.sidebarCardTitle}>{t('crm:score_card')}</h4>
                  <div className={ds.scoreCircle}>
                    <span className={ds.scoreValue}>{score}%</span>
                  </div>
                  <div className={ds.scoreBarWrap}>
                    <div
                      className={ds.scoreBarFill}
                      style={{ width: `${String(score)}%` }}
                    />
                  </div>
                  <div className={ds.temperatureRow}>
                    <span>{TEMPERATURE_EMOJI[temp]}</span>
                    <span className={ds.temperatureLabel}>
                      {t(`crm:temperature_${temp}`)}
                    </span>
                  </div>
                </div>

                {/* Opportunities */}
                <div className={ds.sidebarCard}>
                  <h4 className={ds.sidebarCardTitle}>
                    {t('crm:opportunities')}
                  </h4>
                  <div className={ds.opportunityItem}>
                    <span className={ds.oppLabel}>
                      {t('crm:estimated_budget')}
                    </span>
                    <span className={ds.oppValue}>
                      {prospect.estimated_budget != null
                        ? `${prospect.estimated_budget.toLocaleString()} \u20AC`
                        : '-'}
                    </span>
                  </div>
                  <div className={ds.opportunityItem}>
                    <span className={ds.oppLabel}>
                      {t('crm:estimated_surface')}
                    </span>
                    <span className={ds.oppValue}>
                      {prospect.estimated_surface != null
                        ? `${String(prospect.estimated_surface)} m\u00B2`
                        : '-'}
                    </span>
                  </div>
                </div>

                {/* Metadata */}
                <div className={ds.sidebarCard}>
                  <h4 className={ds.sidebarCardTitle}>{t('crm:metadata')}</h4>
                  <div className={ds.metaItem}>
                    <span className={ds.metaLabel}>{t('crm:created_at')}</span>
                    <span className={ds.metaValue}>
                      {prospect.created_at != null
                        ? new Date(prospect.created_at).toLocaleDateString()
                        : '-'}
                    </span>
                  </div>
                  <div className={ds.metaItem}>
                    <span className={ds.metaLabel}>{t('crm:updated_at')}</span>
                    <span className={ds.metaValue}>
                      {prospect.updated_at != null
                        ? new Date(prospect.updated_at).toLocaleDateString()
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </M.Loading>
    </div>
  );
}

export default CrmProspectFileDetail;
